import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

/** Función que reactiva la guía (driver.js) de una página. */
type GuideStart = () => void

interface ActiveGuideContextValue {
  /** Arranque de la guía de la página actualmente montada, o null si no tiene guía. */
  activeGuideStart: GuideStart | null
  /** Registra (o limpia con null) el arranque de guía de la página actual. */
  setActiveGuide: (start: GuideStart | null) => void
}

const ActiveGuideContext = createContext<ActiveGuideContextValue | null>(null)

/**
 * Mantiene una referencia al arranque de guía de la página activa.
 *
 * Cada página con guía se registra al montarse y se des-registra al desmontarse.
 * El FAB "Ayuda y Recursos" lee ese arranque para su opción "Ver Guía", de modo
 * que un único punto global reactiva la guía correcta según la ruta actual —
 * sin acoplar el FAB a un mapa ruta→guía y cubriendo tanto useGuide como useTour.
 */
export function ActiveGuideProvider({ children }: { children: React.ReactNode }) {
  const [activeGuideStart, setActiveGuideStart] = useState<GuideStart | null>(null)

  const setActiveGuide = useCallback((start: GuideStart | null) => {
    // Envolvemos en una función para que useState no interprete `start` como
    // un updater funcional y lo invoque en lugar de almacenarlo.
    setActiveGuideStart(() => start)
  }, [])

  return (
    <ActiveGuideContext.Provider value={{ activeGuideStart, setActiveGuide }}>
      {children}
    </ActiveGuideContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveGuide() {
  const ctx = useContext(ActiveGuideContext)
  if (!ctx) throw new Error('useActiveGuide debe usarse dentro de ActiveGuideProvider')
  return ctx
}

/**
 * Registra el arranque de guía de la página actual mientras esté montada.
 *
 * `start` debe ser una referencia ESTABLE (useCallback en la página, o el
 * startGuide/startTour ya memoizado de los hooks) para no re-registrar en cada
 * render. Pasar null para páginas sin guía deshabilita "Ver Guía" en el FAB.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRegisterPageGuide(start: GuideStart | null) {
  const { setActiveGuide } = useActiveGuide()
  useEffect(() => {
    setActiveGuide(start ?? null)
    return () => setActiveGuide(null)
  }, [setActiveGuide, start])
}
